import AxiosInstance from '../config/AxiosInstance';

// Mirrors the server enums (customer-order-status/source). The FE never sends `source`.
export const CUSTOMER_ORDER_STATUS = {
  PENDING: 1,
  COMPLETED: 2,
  CANCELLED: 3,
} as const;

export const CUSTOMER_ORDER_SOURCE = {
  POS: 0,
  WEBSITE: 1,
  PHONE: 2,
  WALK_IN: 3,
  INTEGRATION: 4,
  DASHBOARD: 5,
} as const;

export const customerOrderStatusLabel = (status: number): string =>
  ({ 1: 'Pending', 2: 'Completed', 3: 'Cancelled' }) as Record<number, string>[
    status
  ] ?? `Unknown (${status})`;

export const customerOrderSourceLabel = (source: number): string =>
  (
    {
      0: 'POS',
      1: 'Website',
      2: 'Phone Order',
      3: 'Walk-in',
      4: 'Integration',
      5: 'Dashboard',
    } as Record<number, string>
  )[source] ?? `Unknown (${source})`;

export interface CustomerOrderLine {
  id: string;
  parentDetailId: string | null;
  productId: string;
  productName: string;
  productSku: string | null;
  quantity: string;
  price: string;
  discount: string;
  net: string;
  tax: string;
  amount: string;
  position: number;
}

export interface CustomerOrderRow {
  id: string;
  reference: string;
  displayRef: string | null;
  status: number;
  source: number;
  customerName: string;
  customerPhone: string | null;
  requestedFor: string | null;
  notes: string | null;
  amount: string;
  net: string;
  tax: string;
  discount: string;
  cancelReason: string | null;
  placedAt: string;
  createdAt: string;
  locationId: string;
  transactionId: string | null;
  lines?: CustomerOrderLine[];
  allergens?: {
    id: string;
    allergenCode: string;
    allergenName: string;
    note: string | null;
  }[];
}

const BASE = '/api/inventory/customer-orders';

const getListCustomerOrders = async (params: {
  page: number;
  limit: number;
  search?: string;
  status?: number;
  source?: number;
  locationId?: string;
  dateFrom?: string;
  dateTo?: string;
}) => {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  if (params.status !== undefined) query.set('status', String(params.status));
  if (params.source !== undefined) query.set('source', String(params.source));
  if (params.locationId) query.set('locationId', params.locationId);
  if (params.dateFrom) query.set('dateFrom', params.dateFrom);
  if (params.dateTo) query.set('dateTo', params.dateTo);
  return AxiosInstance.get(`${BASE}/findAll?${query.toString()}`);
};

const getCustomerOrder = async (id: string) =>
  AxiosInstance.get(`${BASE}/find-one/${id}`);

const createCustomerOrder = async (payload: {
  locationId: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  requestedFor?: string;
  notes?: string;
  lines: { productId: string; quantity: number; modifierAnswerIds?: string[] }[];
  allergens?: { allergenCode: string; allergenName: string; note?: string }[];
}) => AxiosInstance.post(BASE, payload);

const updateCustomerOrderStatus = async (
  id: string,
  payload: { status: number; cancelReason?: string },
) => AxiosInstance.patch(`${BASE}/${id}/status`, payload);

export {
  getListCustomerOrders,
  getCustomerOrder,
  createCustomerOrder,
  updateCustomerOrderStatus,
};
